import { useEffect } from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { productSlugFromName, slugLastTwoSegments } from './productSlug';

export type OkeTagsValue = {
  owner?: string;
  businessline?: string;
  costcenter?: string;
  contract?: string;
  department?: string;
  criticality?: string;
};

export const TechneOkeTagsFieldSchema = makeFieldSchema({
  output: z =>
    z.object({
      owner: z.string().optional(),
      businessline: z.string().optional(),
      costcenter: z.string().optional(),
      contract: z.string().optional(),
      department: z.string().optional(),
      criticality: z.string().optional(),
    }),
  uiOptions: z => z.object({}),
});

function parseValue(raw: unknown): OkeTagsValue {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as OkeTagsValue;
  }
  return {};
}

export function TechneOkeTagsField(
  props: FieldExtensionComponentProps<OkeTagsValue>,
) {
  const { onChange, formData, formContext, rawErrors, required } = props;
  const value = parseValue(formData);
  const root = (formContext?.formData ?? {}) as Record<string, unknown>;
  const productName = String(root.productName ?? '').trim();
  const environment = String(root.environment ?? '').trim() || 'dev';
  const okeCompartment = (root.okeCompartment ?? {}) as {
    mode?: string;
    treeRootName?: string;
    parentCompartmentLabel?: string;
  };
  const slug = productSlugFromName(productName);
  const prefix = slugLastTwoSegments(slug);
  const paiHint = (() => {
    const label = String(okeCompartment.parentCompartmentLabel ?? '').trim();
    if (label) {
      const parts = label.split('/').map(part => part.trim()).filter(Boolean);
      const last = parts[parts.length - 1] ?? label;
      return last.replace(/^\(root\)\s*/, '').trim() || last;
    }
    return String(okeCompartment.treeRootName ?? '').trim();
  })();

  useEffect(() => {
    const current = parseValue(formData);
    const next: OkeTagsValue = { ...current };
    let changed = false;
    if (!String(next.contract ?? '').trim()) {
      next.contract = 'techne';
      changed = true;
    }
    if (!String(next.department ?? '').trim()) {
      next.department = 'technology';
      changed = true;
    }
    if (!String(next.criticality ?? '').trim()) {
      next.criticality = 'medium';
      changed = true;
    }
    if (!String(next.owner ?? '').trim() && paiHint) {
      next.owner = paiHint;
      changed = true;
    }
    if (changed) {
      onChange(next);
    }
  }, [paiHint, formData, onChange]);

  const patch = (partial: Partial<OkeTagsValue>) =>
    onChange({ ...value, ...partial });

  const fieldError = (rawErrors?.length ?? 0) > 0;

  return (
    <Box marginTop={1} marginBottom={2}>
      <Typography variant="body2" color="textSecondary" paragraph>
        environment e prefix saem do slug. businessunit e owner usam o PAI.
        techne.environment é o ambiente deste template ({environment}).
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        environment = {slug || '…'} · prefix = {prefix || '…'} ·
        techne.environment = {environment}
      </Typography>
      <TextField
        fullWidth
        margin="normal"
        label="Owner (compartment PAI)"
        required={required}
        error={fieldError && !String(value.owner ?? '').trim()}
        value={value.owner ?? ''}
        onChange={event => patch({ owner: event.target.value })}
        helperText="Ex. lyceum. Na árvore nova, o PAI já vem preenchido."
      />
      <TextField
        fullWidth
        margin="normal"
        label="Business line"
        required={required}
        value={value.businessline ?? ''}
        onChange={event => patch({ businessline: event.target.value })}
        helperText="freeform businessline (ex. educacao)"
      />
      <TextField
        fullWidth
        margin="normal"
        label="Cost center"
        required={required}
        value={value.costcenter ?? ''}
        onChange={event => patch({ costcenter: event.target.value })}
        helperText="freeform costcenter (ex. educacao)"
      />
      <TextField
        fullWidth
        margin="normal"
        label="Contract"
        value={value.contract ?? 'techne'}
        onChange={event => patch({ contract: event.target.value })}
        helperText="Padrão techne; altere só se precisar."
      />
      <TextField
        fullWidth
        margin="normal"
        label="Department"
        required={required}
        value={value.department ?? 'technology'}
        onChange={event => patch({ department: event.target.value })}
        helperText="defined tag techne.department"
      />
      <FormControl fullWidth margin="normal">
        <InputLabel id="oke-tag-criticality">Criticality</InputLabel>
        <Select
          labelId="oke-tag-criticality"
          value={value.criticality ?? 'medium'}
          onChange={event =>
            patch({ criticality: String(event.target.value) })
          }
        >
          <MenuItem value="low">low</MenuItem>
          <MenuItem value="medium">medium</MenuItem>
          <MenuItem value="high">high</MenuItem>
        </Select>
        <FormHelperText>defined tag techne.criticality</FormHelperText>
      </FormControl>
    </Box>
  );
}
